import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  useTheme,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import PermissionGroupService from "../../services/doctorServices/PermissionGroupService";
import FileService from "../../services/doctorServices/FileService";
import Headeer from "../../components/Headeer";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FolderIcon from "@mui/icons-material/Folder";
import ArticleIcon from "@mui/icons-material/Article";
import PeopleIcon from "@mui/icons-material/People";

export default function ViewPermissionGroup() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('permissionGroups');
  const navigate = useNavigate();
  const { id } = useParams();
  const permissionService = new PermissionGroupService();
  const fileService = new FileService();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fileNames, setFileNames] = useState({}); // fileId -> fileName mapping

  useEffect(() => {
    fetchGroupData();
  }, [id]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getGroupById(id);
      setGroup(data);
      
      // Fetch file names for shared files
      if (data.shared_files && Array.isArray(data.shared_files) && data.shared_files.length > 0) {
        fetchFileNames(data.shared_files.map(sf => sf.fileId));
      }
    } catch (error) {
      console.error("Error fetching group data:", error);
      navigate("/doctor-permission-groups");
    } finally {
      setLoading(false);
    }
  };

  const fetchFileNames = async (fileIds) => {
    try {
      const files = await fileService.getAllFiles();
      const nameMap = {};
      fileIds.forEach(fileId => {
        const file = files.find(f => f.id === fileId);
        if (file) {
          nameMap[fileId] = file.name;
        }
      });
      setFileNames(nameMap);
    } catch (error) {
      console.error("Error fetching file names:", error);
    }
  };

  const getPermissions = () => {
    if (!group) return [];
    
    // Resource name mapping for translation
    const resourceMap = {
      'own doctors': 'ownDoctors',
      'own patients': 'ownPatients',
      'own form answers': 'ownFormAnswers',
      'own files': 'ownFiles',
      'own forms': 'ownForms',
      'own invitations': 'ownInvitations',
      'own permission groups': 'ownPermissionGroups',
    };
    
    const permissionsList = [];
    Object.keys(group).forEach(key => {
      if (key.startsWith('can_')) {
        // Doctor tipi: sadece scoring_formulas_* alanlarını gizle (create ekranında yok)
        if (group.type === 'doctor' && key.includes('scoring_formulas')) return;
        
        const parts = key.replace('can_', '').split('_');
        const operation = parts.pop();
        const resource = parts.join(' ');
        const resourceKey = resourceMap[resource] || resource;
        
        permissionsList.push({
          key,
          resource: resourceKey,
          operation: operation,
          enabled: group[key],
        });
      }
    });
    
    return permissionsList;
  };

  const groupPermissionsByResource = () => {
    const permissions = getPermissions();
    const grouped = {};
    
    permissions.forEach(perm => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <Box m="20px">
        <Headeer title={t('loading')} subtitle={t('pleaseWait')} />
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={50} sx={{ color: colors.blueAccent[500], mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.grey[300] }}>
              {t('loadingGroupData')}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!group) {
    return null;
  }

  const groupedPermissions = groupPermissionsByResource();
  const enabledCount = getPermissions().filter(p => p.enabled).length;
  const totalCount = getPermissions().length;

  return (
    <Box m="20px">
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate("/doctor-permission-groups")}
            sx={{
              backgroundColor: `${colors.grey[700]}40`,
              "&:hover": {
                backgroundColor: `${colors.grey[600]}60`,
              },
            }}
          >
            <ArrowBackIcon sx={{ color: colors.grey[300] }} />
          </IconButton>
          
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: colors.grey[100],
                }}
              >
                {group.label}
              </Typography>
              {group.is_default && (
                <Tooltip title={t('defaultGroupTooltip')}>
                  <Chip
                    icon={<LockIcon sx={{ fontSize: 14 }} />}
                    label={t('default')}
                    size="small"
                    sx={{
                      backgroundColor: `${colors.blueAccent[800]}40`,
                      color: colors.blueAccent[300],
                      fontWeight: 600,
                      border: `1px solid ${colors.blueAccent[500]}40`,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
              {group.description || t('noDescription')}
            </Typography>
          </Box>
        </Box>

        {!group.is_default && (
          <Tooltip title={t('editGroup')}>
            <IconButton
              onClick={() => navigate(`/doctor-permission-groups/edit/${group.id}`)}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: `${colors.greenAccent[700]}40`,
                border: `1px solid ${colors.greenAccent[500]}40`,
                "&:hover": {
                  backgroundColor: `${colors.greenAccent[600]}60`,
                  transform: "scale(1.05)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <EditIcon sx={{ fontSize: 22, color: colors.greenAccent[300] }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Group Info */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
              height: "100%",
            }}
          >
            <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700, mb: 3 }}>
              📋 {t('groupDetails')}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Type */}
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 1 }}>
                  {t('groupType')}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MedicalServicesIcon sx={{ color: "white", fontSize: 22 }} />
                  </Box>
                  <Chip
                    label={t('doctor')}
                    sx={{
                      backgroundColor: colors.greenAccent[700],
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ backgroundColor: `${colors.grey[700]}40` }} />

              {/* Internal Name */}
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                  {t('internalName')}
                </Typography>
                <Typography variant="body1" sx={{ color: colors.grey[100], fontWeight: 600, fontFamily: "monospace" }}>
                  {group.name}
                </Typography>
              </Box>

              <Divider sx={{ backgroundColor: `${colors.grey[700]}40` }} />

              {/* Created By */}
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                  {t('createdBy')}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: colors.grey[100], 
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  {group.created_by ? `${t('doctorPrefix')}${group.created_by.substring(0, 8)}...` : t('systemDefault')}
                </Typography>
              </Box>

              <Divider sx={{ backgroundColor: `${colors.grey[700]}40` }} />

              {/* Permissions Count */}
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                  {t('activePermissions')}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h4" sx={{ color: colors.greenAccent[400], fontWeight: 700 }}>
                    {enabledCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                    / {totalCount} {t('total')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Permissions Matrix */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
            }}
          >
            <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700, mb: 3 }}>
              🔐 {t('permissionsOverview')}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {Object.entries(groupedPermissions).map(([resource, permissions]) => (
                <Card
                  key={resource}
                  sx={{
                    background: `${colors.primary[600]}80`,
                    borderRadius: "12px",
                    border: `1px solid ${colors.grey[700]}40`,
                  }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.grey[100],
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: "0.95rem",
                      }}
                    >
                      {t(resource)}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {permissions.map(perm => (
                        <Chip
                          key={perm.key}
                          icon={
                            perm.enabled ? (
                              <CheckCircleIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          label={t(perm.operation)}
                          size="small"
                          sx={{
                            backgroundColor: perm.enabled 
                              ? `${colors.greenAccent[700]}80`
                              : `${colors.grey[700]}80`,
                            color: perm.enabled 
                              ? colors.greenAccent[100]
                              : colors.grey[300],
                            fontWeight: 600,
                            border: `1px solid ${perm.enabled 
                              ? colors.greenAccent[500] 
                              : colors.grey[600]}40`,
                            "& .MuiChip-icon": {
                              color: perm.enabled 
                                ? colors.greenAccent[300]
                                : colors.grey[400],
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Shared Files Section */}
        {group.shared_files && Array.isArray(group.shared_files) && group.shared_files.length > 0 && (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
                border: `1px solid ${colors.grey[800]}30`,
                borderRadius: "16px",
              }}
            >
              <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700, mb: 3 }}>
                {t('sharedFilesWithCount', { count: group.shared_files.length })}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {group.shared_files.map((sharedFile, index) => (
                  <Card
                    key={sharedFile.fileId || index}
                    sx={{
                      background: `${colors.primary[600]}80`,
                      borderRadius: "12px",
                      border: `1px solid ${colors.greenAccent[500]}40`,
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      {/* File Header */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                        <FolderIcon sx={{ color: colors.greenAccent[400], fontSize: 28 }} />
                        <Box>
                          <Typography variant="h6" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                            {fileNames[sharedFile.fileId] || sharedFile.fileName || `${t('file')} ${sharedFile.fileId.substring(0, 8)}...`}
                          </Typography>
                          {fileNames[sharedFile.fileId] && (
                            <Typography variant="caption" sx={{ color: colors.grey[400], fontFamily: "monospace" }}>
                              {t('idLabel')} {sharedFile.fileId.substring(0, 8)}...
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ mb: 2, backgroundColor: `${colors.grey[700]}40` }} />

                      {/* File Level Permissions */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <FolderIcon sx={{ color: colors.blueAccent[400], fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ color: colors.grey[200], fontWeight: 600 }}>
                            {t('filePermissions')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {['view', 'update', 'delete'].map(op => (
                            <Chip
                              key={op}
                              icon={sharedFile[`can_${op}`] ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                              label={t(op)}
                              size="small"
                              sx={{
                                backgroundColor: sharedFile[`can_${op}`] 
                                  ? `${colors.greenAccent[700]}80`
                                  : `${colors.grey[700]}80`,
                                color: sharedFile[`can_${op}`] 
                                  ? colors.greenAccent[100]
                                  : colors.grey[300],
                                fontWeight: 600,
                                border: `1px solid ${sharedFile[`can_${op}`] 
                                  ? colors.greenAccent[500] 
                                  : colors.grey[600]}40`,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Forms Permissions */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <ArticleIcon sx={{ color: colors.greenAccent[400], fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ color: colors.grey[200], fontWeight: 600 }}>
                            {t('formsPermissions')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {['view', 'update', 'delete', 'add', 'send', 'fill'].map(op => (
                            <Chip
                              key={op}
                              icon={sharedFile.forms?.[`can_${op}`] ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                              label={t(op)}
                              size="small"
                              sx={{
                                backgroundColor: sharedFile.forms?.[`can_${op}`] 
                                  ? `${colors.greenAccent[700]}80`
                                  : `${colors.grey[700]}80`,
                                color: sharedFile.forms?.[`can_${op}`] 
                                  ? colors.greenAccent[100]
                                  : colors.grey[300],
                                fontWeight: 600,
                                border: `1px solid ${sharedFile.forms?.[`can_${op}`] 
                                  ? colors.greenAccent[500] 
                                  : colors.grey[600]}40`,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Patients Permissions */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <PeopleIcon sx={{ color: colors.blueAccent[400], fontSize: 18 }} />
                          <Typography variant="subtitle2" sx={{ color: colors.grey[200], fontWeight: 600 }}>
                            {t('patientsPermissions')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {['view', 'update', 'delete', 'add'].map(op => (
                            <Chip
                              key={op}
                              icon={sharedFile.patients?.[`can_${op}`] ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                              label={t(op)}
                              size="small"
                              sx={{
                                backgroundColor: sharedFile.patients?.[`can_${op}`] 
                                  ? `${colors.greenAccent[700]}80`
                                  : `${colors.grey[700]}80`,
                                color: sharedFile.patients?.[`can_${op}`] 
                                  ? colors.greenAccent[100]
                                  : colors.grey[300],
                                fontWeight: 600,
                                border: `1px solid ${sharedFile.patients?.[`can_${op}`] 
                                  ? colors.greenAccent[500] 
                                  : colors.grey[600]}40`,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}




